/**
 * Helpers para ler claims customizadas do JWT do Supabase Auth.
 *
 * As claims (`is_superadmin`, `permissions`, `clinic_ids`, `current_clinic_id`,
 * `role_key`) são injetadas pelo hook Postgres
 * `public.custom_access_token_hook` — ver migration 0027 e `docs/auth-hooks.md`.
 *
 * Decodificamos o payload base64url localmente para evitar uma round-trip extra
 * ao banco só para checar se o user é superadmin. Não precisamos verificar a
 * assinatura no cliente: RLS no Postgres é quem faz a verificação real a cada
 * request — o JWT local serve apenas para decisões de UI (mostrar/esconder rota).
 */

/** Estados possíveis de `subscriptions.status` que podem aparecer no JWT.
 * Espelha o enum da migration 0007_billing.sql + injetado em
 * `current_clinic_subscription_status` via `custom_access_token_hook`
 * estendido em migration 0063. Quando user não tem subscription
 * (superadmin sem clinic, lead que virou user, etc), o claim é `null`.
 *
 * Significado para o gate de pagamento (Modelo C híbrido — ADR 0009):
 *   - 'trialing' / 'active' / 'past_due' → libera acesso
 *   - 'incomplete' → redireciona pra /clinic/payment-pending
 *     (gate de pagamento ativo, primeira fatura não confirmada)
 *   - 'canceled' / 'paused' → redireciona pra /clinic/suspended
 *   - null → libera (superadmin tem fast-path, user sem clinic não
 *     chega nas rotas protegidas)
 */
export type ClinicSubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'incomplete'
  | 'canceled'
  | 'paused';

export interface KmgestaoJwtClaims {
  sub: string;
  email?: string;
  aal?: 'aal1' | 'aal2';
  exp?: number;
  is_superadmin?: boolean;
  permissions?: readonly string[];
  clinic_ids?: readonly string[];
  current_clinic_id?: string | null;
  role_key?: string | null;
  /** Status da subscription da clinic atual — populado pelo hook
   * (migration 0063). Consumido pelo `RequireClinicSubscriptionActive`. */
  current_clinic_subscription_status?: ClinicSubscriptionStatus | null;
}

/**
 * Decodifica o payload de um JWT sem verificar a assinatura. Retorna `null`
 * para qualquer erro de parsing — evita throw e facilita uso em hooks.
 */
export function decodeJwtClaims(accessToken: string | null | undefined): KmgestaoJwtClaims | null {
  if (!accessToken) return null;
  const parts = accessToken.split('.');
  if (parts.length < 2) return null;
  const payload = parts[1];
  if (!payload) return null;

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = atob(padded);
    const parsed = JSON.parse(decoded) as KmgestaoJwtClaims;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Retorna `true` se o JWT do usuário contém `is_superadmin: true`. Em caso de
 * qualquer dúvida (claim ausente, parsing falhou), retorna `false` — fail
 * closed. O fallback (ler `profiles.is_superadmin`) é feito pelo Postgres em
 * `public.is_superadmin()`; aqui não fazemos fallback para evitar exposição
 * de segredos do schema no cliente.
 */
export function isSuperadminFromToken(accessToken: string | null | undefined): boolean {
  const claims = decodeJwtClaims(accessToken);
  return claims?.is_superadmin === true;
}

/**
 * Retorna `true` se o JWT indica que o usuário é admin/owner de **alguma**
 * clínica (pela claim `role_key`). Aceita também superadmin — a UI da
 * clínica deve estar acessível para diagnóstico e suporte. A verificação
 * real por clínica específica roda no Postgres via `is_clinic_admin(id)`.
 */
export function isClinicAdminFromToken(accessToken: string | null | undefined): boolean {
  const claims = decodeJwtClaims(accessToken);
  if (claims?.is_superadmin === true) return true;
  const key = claims?.role_key;
  return key === 'clinic_owner' || key === 'clinic_admin';
}

/**
 * Resolve a "decisão de gate de pagamento" para o JWT atual (Modelo C
 * híbrido — ADR 0009). Retorna:
 *
 *   - 'allow'           → user passa (status ok ou superadmin ou sem clinic)
 *   - 'payment_pending' → bloqueia, redireciona pra /clinic/payment-pending
 *   - 'suspended'       → bloqueia, redireciona pra /clinic/suspended
 *
 * Decisão centralizada aqui pra ser consumida tanto pelo guard de rota
 * quanto por componentes de UI (ex: badges/avisos no shell). Sem
 * lookup ao banco — JWT é fonte da verdade pra esse semáforo.
 */
export type ClinicPaymentGateDecision = 'allow' | 'payment_pending' | 'suspended';

export function resolveClinicPaymentGate(
  accessToken: string | null | undefined,
): ClinicPaymentGateDecision {
  const claims = decodeJwtClaims(accessToken);
  // Superadmin nunca é bloqueado pelo gate de pagamento — ela
  // precisa acessar painéis cross-clinic.
  if (claims?.is_superadmin === true) return 'allow';

  const status = claims?.current_clinic_subscription_status ?? null;
  if (status === null || status === undefined) return 'allow'; // sem clinic
  if (status === 'incomplete') return 'payment_pending';
  if (status === 'canceled' || status === 'paused') return 'suspended';
  // 'trialing', 'active', 'past_due' → libera. past_due é fase 2
  // (suspensão por inadimplência) — comportamento atual: deixa entrar
  // mas UI pode mostrar aviso.
  return 'allow';
}
